import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Divider, Typography, CircularProgress } from "@mui/material";
import { amendApplication, getAmendmentStatus } from "apis/controllers/applications";
import { useNavigate, useParams } from "react-router-dom";
import "./Amendment.css";
import { CssTextField } from "qubCommon/Inputs";
import { Skeleton, Box } from "@mui/material";

const LoadingSkeleton = () => (
  <Box sx={{ width: "100%", padding: 2 }}>
    <Skeleton sx={{ width: "50%", height: "44px" }} />
    <Skeleton sx={{ width: "40%", height: "35px" }} />
    <Skeleton sx={{ width: "100%", height: "30px" }} />
    <Skeleton sx={{ width: "100%", height: "120px" }} />
  </Box>
);

export default function Amendment(props) {
  const { open, handleClose } = props;
  const ammendmentForm = useForm();
  const [loading, setLoading] = useState(true);
  const [amendmentOptions, setAmendmentOptions] = useState(null);
  const [title, setTitle] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [checkedOption, setCheckedOption] = useState(null);
  const [error, setError] = useState(false);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false); // 👈 NEW
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const res = await getAmendmentStatus(id);
      setTitle(res?.data?.title);
      setCurrentStatus(res?.data?.currentStatus);
      setAmendmentOptions(res?.data?.status);
    } catch (e) {
      console.error(e);
      setTitle(null);
      setCurrentStatus(null);
      setAmendmentOptions(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setAmendmentOptions(null);
      setTitle(null);
      setCurrentStatus(null);
      setCheckedOption(null);
      setComments("");
      setLoading(true);
      setError(null);
      setSubmitting(false);
    }
    if (open) fetchOptions();
  }, [open]);

  const handleCheckboxChange = (id) => {
    setError(null);
    setCheckedOption(id);
  };

  const handleCommentsChange = (e) => setComments(e.target.value);

  const handleSubmit = async () => {
    if (!checkedOption) {
      setError("Δεν έχετε επιλέξει ενέργεια!");
      return;
    }

    try {
      setSubmitting(true); // 👈 Start loading
      const res = await amendApplication({
        id,
        newStatusId: checkedOption,
        comments,
      });

      if (res?.status === "success") navigate(0);
      else setError(res?.message || "Προέκυψε σφάλμα!");
    } catch (e) {
      console.error(e);
      setError("Προέκυψε σφάλμα!");
    } finally {
      setSubmitting(false); // 👈 Stop loading
    }
  };

  return (
    <FormProvider {...ammendmentForm}>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="md"
      >
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <DialogTitle sx={{ m: 0, p: 2, fontSize: "16px" }}>
              Μεταβολή Κατάστασης - {title}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <Divider />
            <DialogContent sx={{ width: "100%", mx: "auto" }}>
              <div className="form-container">
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Τρέχουσα κατάσταση αίτησης: <b>{currentStatus}</b>
                </Typography>
                {error && (
                  <Typography color="error" sx={{ mb: 1, fontWeight: "bold" }}>
                    {error}
                  </Typography>
                )}

                {amendmentOptions?.length === 0 ? (
                  <div style={{ textAlign: "center", marginTop: 40 }}>
                    <b>Δεν βρέθηκαν διαθέσιμες ενέργειες!</b>
                  </div>
                ) : (
                  <>
                    <table className="actions-table">
                      <thead>
                        <tr>
                          <th>Διαθέσιμη Ενέργεια</th>
                          <th>Νέα κατάσταση αίτησης</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amendmentOptions?.map((option) => (
                          <tr key={option?.id}>
                            <td>
                              <div className="checkbox-container">
                                <input
                                  type="checkbox"
                                  id={option?.id}
                                  checked={checkedOption === option.id}
                                  onChange={() => handleCheckboxChange(option.id)}
                                  disabled={submitting} // 👈 disable during submit
                                />
                                {option?.description}
                              </div>
                            </td>
                            <td>{option?.title}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="form-group">
                      <label htmlFor="comments">Σχόλια</label>
                      <CssTextField
                        multiline
                        minRows={5}
                        fullWidth
                        fixedPadding={8}
                        onChange={handleCommentsChange}
                        disabled={submitting} // 👈 disable input during submit
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        marginTop: "16px",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ color: "white" }}
                        onClick={handleClose}
                        disabled={submitting}
                      >
                        Επιστροφή
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        sx={{ minWidth: 140 }}
                      >
                        {submitting ? (
                          <>
                            <CircularProgress
                              size={20}
                              color="inherit"
                              sx={{ mr: 1 }}
                            />
                            Αποθήκευση...
                          </>
                        ) : (
                          "Αποθήκευση"
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    </FormProvider>
  );
}
